import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocalState, useOnline } from '@ndrc/app-sdk/client';
import { useDesk } from '../desk/context';
import { callCore, mutateCore } from '../lib/rpc';
import { t } from '../messages';
export function SubmissionForm({
  environmentId,
  kind,
  externalId,
  sourceVersion,
}: {
  environmentId: string;
  kind: string;
  externalId: string;
  sourceVersion: string;
}) {
  const { me, observing, workspace } = useDesk();
  const online = useOnline(),
    cache = useQueryClient();
  const activityQuery = useQuery({
    queryKey: ['core', 'external.submissionActivities', environmentId],
    queryFn: () =>
      callCore<{ activities: { id: string; title: string }[] }>('external.submissionActivities', {
        environmentId,
      }),
    enabled: me.role === 'student' && !observing && !workspace?.readOnly,
    refetchInterval: 5000,
  });
  const activities = activityQuery.data?.activities ?? [];
  const [draft, setDraft, stored] = useLocalState(
    `submission:${me.id}:${environmentId}:${kind}:${externalId}`,
    { justification: '', activityId: '', sourceVersion, requestKey: crypto.randomUUID() },
  );
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const changed = draft.sourceVersion !== sourceVersion;
  const unavailableActivity = Boolean(
    draft.activityId &&
    (activityQuery.isError || !activities.some((activity) => activity.id === draft.activityId)),
  );
  async function submit() {
    if (
      busy ||
      !online ||
      observing ||
      workspace?.readOnly ||
      changed ||
      unavailableActivity ||
      !draft.justification.trim()
    )
      return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await mutateCore<{ id: string; version: number }>(
        'external.submit',
        {
          environmentId,
          kind,
          externalId,
          sourceVersion: draft.sourceVersion,
          justification: draft.justification,
          ...(draft.activityId ? { activityId: draft.activityId } : {}),
          requestKey: draft.requestKey,
        },
        draft.requestKey,
      );
      setDraft({ ...draft, justification: '', requestKey: crypto.randomUUID() });
      setNotice(t('cms.submission.sent', { version: result.version }));
      await cache.invalidateQueries({ queryKey: ['core', 'external.submissions'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('cms.submission.error'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="cms-submission-form">
      <h4>{t('cms.submission.submitTitle')}</h4>
      <p>{t('cms.submission.submitHelp')}</p>
      <label>
        {t('cms.submission.justification')}
        <textarea
          rows={4}
          maxLength={6000}
          value={draft.justification}
          disabled={busy}
          onChange={(e) =>
            setDraft({ ...draft, justification: e.target.value, requestKey: crypto.randomUUID() })
          }
        />
      </label>
      <label>
        {t('cms.submission.activity')}
        <select
          value={draft.activityId}
          disabled={busy}
          onChange={(e) =>
            setDraft({ ...draft, activityId: e.target.value, requestKey: crypto.randomUUID() })
          }
        >
          <option value="">{t('cms.submission.free')}</option>
          {activities.map((activity) => (
            <option key={activity.id} value={activity.id}>
              {activity.title}
            </option>
          ))}
          {draft.activityId && !activities.some((activity) => activity.id === draft.activityId) && (
            <option value={draft.activityId} disabled>
              {t('cms.submission.previousActivity')}
            </option>
          )}
        </select>
      </label>
      {activityQuery.isPending && <p role="status">{t('cms.submission.activitiesLoading')}</p>}
      {activityQuery.isError && <p role="alert">{t('cms.submission.activitiesError')}</p>}
      {changed && (
        <p role="alert">
          {t('cms.submission.changed')}{' '}
          <button
            type="button"
            className="ndrc-button"
            onClick={() => setDraft({ ...draft, sourceVersion, requestKey: crypto.randomUUID() })}
          >
            {t('cms.submission.useCurrent')}
          </button>
        </p>
      )}
      {!stored && <p role="alert">{t('cms.submission.localError')}</p>}
      {error && (
        <p role="alert" className="ndrc-error">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      <button
        type="button"
        className="ndrc-button"
        disabled={
          busy ||
          !online ||
          Boolean(observing) ||
          workspace?.readOnly ||
          changed ||
          !draft.justification.trim() ||
          unavailableActivity
        }
        onClick={() => {
          void submit();
        }}
      >
        {busy ? t('cms.working') : t('cms.submission.submit')}
      </button>
    </div>
  );
}
